// DEPLOY-PROBE-bump

/**
 * PaySlipPreview Component
 * 
 * Main layout component for the payslip preview system.
 * Provides a tabbed interface to switch between:
 * - Office Employee (本社) payslip preview
 * - Construction Worker (現場) payslip preview
 * 
 * Features:
 * - Responsive design with different layouts for mobile and desktop
 * - Tab navigation between employee types
 * - Dynamic mobile detection based on window width
 * - Separate layout structures for mobile (simplified) and desktop (container-based)
 * 
 * The component automatically adjusts its layout based on screen size:
 * - Mobile (≤768px): Simplified layout without container wrappers
 * - Desktop (>768px): Full layout with container wrappers
 * 
 * @component
 */

import React, { useState } from 'react';
import './css/PaySlipPreview.css';
import { OfficeEmpPreview } from './components/OfficeEmp/OfficeEmpPreview';
import { ConstructionEmpPreview } from './components/ConstructionEmp/ConstructionEmpPreview';

/**
 * PaySlipPreview Component
 * 
 * Main entry point for the payslip preview interface.
 * Manages tab switching and responsive layout rendering.
 * 
 * @returns React functional component
 */
export const PaySlipPreview: React.FC<{
  /** @deprecated prefer {@link hideOfficePayslipTab} */
  isConstructionAdminOnly?: boolean;
  hideOfficePayslipTab?: boolean;
  forcedAssignedGenba?: string;
  /** 担当現場管理者: 給与明細プレビュー shows salary only (no other employees' 賞与). */
  hideBonusTab?: boolean;
}> = ({ isConstructionAdminOnly = false, hideOfficePayslipTab, forcedAssignedGenba, hideBonusTab = false }) => {
  const hideOffice = hideOfficePayslipTab ?? isConstructionAdminOnly;
  const [activeTab, setActiveTab] = useState(hideOffice ? 'construction' : 'office');
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    if (hideOffice) {
      setActiveTab('construction');
    }
  }, [hideOffice]);

  /**
   * Check for mobile device and update state on resize.
   * 
   * Monitors window width to detect mobile devices (≤768px).
   * Adds resize event listener to update mobile state when window size changes.
   * Cleans up event listener on component unmount.
   */
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check on mount
    checkMobile();
    // Listen for window resize events
    window.addEventListener('resize', checkMobile);
    
    // Cleanup: remove event listener on unmount
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Render content directly on mobile without container wrappers.
   * 
   * Mobile layout uses simplified structure:
   * - Main content container with mobile-layout class
   * - Tab navigation for switching between office and construction
   * - Tab content area with conditional rendering based on activeTab
   */
  if (isMobile) {
    return (
      <div className="main-content mobile-layout">
        {/* Page Title */}
        <h3>給与明細プレビュー</h3>
        
        {/* Tab Navigation - Switch between Office (本社) and Construction (現場) */}
        <nav className="tab-navigation">
          {!hideOffice && (
            <button 
              className={`tab-button ${activeTab === 'office' ? 'active' : ''}`}
              onClick={() => setActiveTab('office')}
            >
              本社
            </button>
          )}
          <button 
            className={`tab-button ${activeTab === 'construction' ? 'active' : ''}`}
            onClick={() => setActiveTab('construction')}
          >
            現場
          </button>
        </nav>
        
        {/* Tab Content Area - Conditionally renders based on activeTab */}
        <div className="tab-content">
          {/* Office Employee Preview - Rendered when 'office' tab is active */}
          {activeTab === 'office' && (
            <div className="tab-panel">
              <OfficeEmpPreview />
            </div>
          )}
          
          {/* Construction Worker Preview - Rendered when 'construction' tab is active */}
          {activeTab === 'construction' && (
            <div className="tab-panel">
              <ConstructionEmpPreview forcedAssignedGenba={forcedAssignedGenba} hideBonusTab={hideBonusTab} />
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * Desktop layout with container wrappers.
   * 
   * Desktop layout uses full structure with container:
   * - Main content container
   * - Tab container wrapper
   * - Tab navigation for switching between office and construction
   * - Tab content area with conditional rendering based on activeTab
   */
  return (
    <div className="main-content">
      {/* Page Title */}
      <h3>給与明細プレビュー</h3>
      
      <div className="tab-container">
        <nav className="tab-navigation">
          {!hideOffice && (
            <button 
              className={`tab-button ${activeTab === 'office' ? 'active' : ''}`}
              onClick={() => setActiveTab('office')}
            >
              本社
            </button>
          )}
          <button 
            className={`tab-button ${activeTab === 'construction' ? 'active' : ''}`}
            onClick={() => setActiveTab('construction')}
          >
            現場
          </button>
        </nav>
        
        <div className="tab-content">
          {activeTab === 'office' && (
            <div className="tab-panel">
              <OfficeEmpPreview />
            </div>
          )}
          {activeTab === 'construction' && (
            <div className="tab-panel">
              <ConstructionEmpPreview forcedAssignedGenba={forcedAssignedGenba} hideBonusTab={hideBonusTab} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 